import type { Audiences, CmsData, ParagraphType, TimeControl } from '../../interfaces';
import { createFilterForTimeControl, createFilterForCurrentAudiences } from './filterResult';

/** If the desired paragraph item is taken from the librabry, it needs to be unwrapped. */
const getItem = (item: ParagraphType): ParagraphType => {
  if (
    item.type === 'from_library' &&
    item.paragraphs_library_item &&
    item.paragraphs_library_item.length > 0 &&
    item.paragraphs_library_item[0]
  ) {
    return item.paragraphs_library_item[0].paragraph[0];
  }
  return item;
};

/**
 * Recursively filters paragraphs and subparagraphs based on given TestDate and Audience.
 */
export const filterParagraphs = (
  paragraphs: ParagraphType[],
  testDate?: string | string[],
  audiences?: Audiences[],
): ParagraphType[] => {
  const filterTimeControl = createFilterForTimeControl(testDate);
  const filterAudience = audiences ? createFilterForCurrentAudiences(audiences) : () => true;

  return (
    paragraphs
      // first filter out invalid paragraphs
      .filter((itemOrLibraryItem) => {
        const item = getItem(itemOrLibraryItem);
        return filterTimeControl(item as TimeControl) && filterAudience(item);
      })
      .reduce(
        (acc, itemOrLibraryItem) => {
          const item = getItem(itemOrLibraryItem);

          // if there are sub paragraphs, we also need to take care of them
          if ('paragraph' in item && item.paragraph && item.paragraph.length > 0) {
            const filteredSubParagraphs = filterParagraphs(
              item.paragraph as ParagraphType[],
              testDate,
              audiences,
            );

            // if there are results, we can add pass them back to the parent level
            if (filteredSubParagraphs.length > 0) {
              return [
                ...acc,
                {
                  ...item,
                  paragraph: filteredSubParagraphs,
                } as ParagraphType,
              ];
            }

            // we have no valid sub paragraphs and we skip empty groups/containers
            if (['group_localizable', 'content_container', 'content_layout'].includes(item.type)) {
              return acc;
            }

            delete item.paragraph; // Remove empty paragraph
          }
          return [...acc, item];
        },
        [] as CmsData['paragraph'],
      )
  );
};

/** Applies time-control/audience filtering to a full CmsData payload's paragraphs. */
export const filteredContent = (
  content: CmsData,
  testDate?: string | string[],
  audiences?: Audiences[],
): CmsData => ({
  ...content,
  paragraph: filterParagraphs(content.paragraph, testDate, audiences),
});
